import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Crown, Users } from 'lucide-react';
import { getSellerDevice, getSellerDevices } from '../api/seller.js';
import type { SellerRegistrationItem } from '@m8-app/contracts';

function fmtDate(iso: string) {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

export function UsersPage() {
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const deviceId     = params.get('device');
  const serial       = params.get('serial');
  const isDeviceView = !!deviceId;

  // device-specific view
  const { data: deviceData, isLoading: deviceLoading } = useQuery({
    queryKey: ['seller-device', deviceId],
    queryFn:  () => getSellerDevice(deviceId!),
    enabled:  isDeviceView,
  });

  // global view — all registrations
  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ['seller-devices'],
    queryFn:  getSellerDevices,
    enabled:  !isDeviceView,
  });

  const reg      = deviceData?.registration;
  const allRegs  = (allData?.registrations ?? []) as SellerRegistrationItem[];
  const isLoading = isDeviceView ? deviceLoading : allLoading;

  return (
    <div className="p-6">
      {isDeviceView && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors mb-5 -ml-0.5"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}

      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-900">
          {isDeviceView ? 'Device Users' : 'Users'}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {isDeviceView
            ? <>Users linked to <span className="font-mono font-semibold text-slate-700">{serial ?? deviceId}</span></>
            : 'All registered customers across every device.'}
        </p>
      </div>

      {isLoading && (
        <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
      )}

      {/* ── Device-specific view ── */}
      {isDeviceView && !isLoading && reg && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-10">#</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Registered</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">1</td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <Crown className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{reg.customerName ?? '—'}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-md ring-1 bg-blue-50 text-blue-700 ring-blue-200/60">
                    Main User
                  </span>
                </td>
                <td className="px-5 py-3.5 text-sm text-slate-600">{reg.customerMobile}</td>
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{fmtDate(reg.registeredAt)}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-5 py-3 text-xs text-slate-400 border-t border-slate-100">
            Additional members are added after the customer claims this device in the mobile app.
          </p>
        </div>
      )}

      {/* ── Global view — all customers ── */}
      {!isDeviceView && !isLoading && (
        allRegs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center mb-4">
              <Users className="w-7 h-7 text-slate-300" />
            </div>
            <p className="text-slate-700 font-semibold mb-1">No users yet</p>
            <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
              Users appear here once you register devices for customers.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-10">#</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Device</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Registered</th>
                </tr>
              </thead>
              <tbody>
                {allRegs.map((item, i) => (
                  <tr key={item.registrationId} className={i < allRegs.length - 1 ? 'border-b border-slate-50' : ''}>
                    <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                          <Crown className="w-3.5 h-3.5 text-blue-500" />
                        </div>
                        <span className="text-sm font-semibold text-slate-900">{item.customerName ?? '—'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-md ring-1 bg-blue-50 text-blue-700 ring-blue-200/60">
                        Main User
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">{item.customerMobile}</td>
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/devices/${item.registrationId}`)}
                        className="font-mono text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline underline-offset-2 transition-colors"
                      >
                        {item.serialNumber}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{fmtDate(item.registeredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
